// The finish camera: which finishes are close enough for a photo, and the times the photo shows. Pure.
// The signal gives official times in tenths of a second only. The photo is decorative: the game invents
// nanosecond times inside each cat's official tenth, in the order of the official places, so the photo
// always agrees with the official results and never needs anything more from the signal.
import type { CatId, RaceSnapshot } from '../race/types';
import { CAT_DISPLAY } from './cats';
import { ordinal } from './format';

type Result = NonNullable<RaceSnapshot['results']>[number];

/** Cats finishing under half a second apart get a photo: 4 tenths or less in the official times. */
export const PHOTO_FINISH_TENTHS = 5;
const NS_PER_TENTH = 100_000_000;
/** Invented offsets stay strictly inside the official tenth, so they round back to the official time. */
const MAX_OFFSET_NS = NS_PER_TENTH / 2 - 1;

export interface PhotoCat { id: CatId; place: number; seconds: number; /** crossing time, ns from post time */ ns: number }
/** One photo: cats in place order (two or more), each under half a second from the one before. */
export interface PhotoFinish { cats: PhotoCat[] }

const tenths = (seconds: number) => Math.round(seconds * 10);

/** Official results grouped into close finishes, best places first. Disqualified cats never count. */
export function closeFinishGroups(results: readonly Result[]): Result[][] {
  const ranked = results.filter((r) => !r.dsq).sort((a, b) => a.place - b.place);
  const groups: Result[][] = [];
  let group: Result[] = [];
  for (const r of ranked) {
    const prev = group[group.length - 1];
    if (prev && tenths(r.seconds) - tenths(prev.seconds) < PHOTO_FINISH_TENTHS) group.push(r);
    else { if (group.length > 1) groups.push(group); group = [r]; }
  }
  if (group.length > 1) groups.push(group);
  return groups;
}

export function hasPhotoFinish(results: readonly Result[]): boolean {
  return closeFinishGroups(results).length > 0;
}

/** The photos for a race. The same race and results always give the same times. */
export function photoFinishes(results: readonly Result[], raceNo: number): PhotoFinish[] {
  return closeFinishGroups(results).map((group) => {
    const rand = mulberry32(hash(`${raceNo}|${group.map((r) => `${r.id}:${tenths(r.seconds)}`).join('|')}`));
    const cats: PhotoCat[] = [];
    // Cats sharing an official tenth get distinct offsets inside it, earliest to the better place.
    for (let i = 0; i < group.length;) {
      const t = tenths(group[i]!.seconds);
      let j = i;
      while (j < group.length && tenths(group[j]!.seconds) === t) j++;
      const offsets = distinctOffsets(j - i, rand);
      for (let k = i; k < j; k++) {
        const r = group[k]!;
        cats.push({ id: r.id, place: r.place, seconds: r.seconds, ns: t * NS_PER_TENTH + offsets[k - i]! });
      }
      i = j;
    }
    return { cats };
  });
}

/** "Nemo Pongo wins by 0.083 412 907 s" or "Tyyne takes 3rd by …". */
export function verdict(p: PhotoFinish): string {
  const [a, b] = p.cats;
  const by = formatMarginNs(b!.ns - a!.ns);
  const who = CAT_DISPLAY[a!.id].name;
  return a!.place === 1 ? `${who} wins by ${by}` : `${who} takes ${ordinal(a!.place)} by ${by}`;
}

function distinctOffsets(n: number, rand: () => number): number[] {
  for (;;) {
    const out = Array.from({ length: n }, () => Math.floor(rand() * (2 * MAX_OFFSET_NS + 1)) - MAX_OFFSET_NS).sort((a, b) => a - b);
    if (out.every((v, i) => i === 0 || v > out[i - 1]!)) return out;
  }
}

/** 9 digits in groups of three, joined with narrow no-break spaces: "083 412 907". */
function nanoDigits(ns: number): string {
  return String(ns).padStart(9, '0').replace(/(\d{3})(?=\d)/g, '$1\u202F');
}

/** A crossing time to the nanosecond, m:ss.nnn nnn nnn. */
export function formatNs(ns: number): string {
  const whole = Math.floor(ns / 1e9);
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}.${nanoDigits(ns - whole * 1e9)}`;
}

/** A margin between two cats to the nanosecond, in seconds: 0.083 412 907 s. */
export function formatMarginNs(ns: number): string {
  const whole = Math.floor(ns / 1e9);
  return `${whole}.${nanoDigits(ns - whole * 1e9)} s`;
}

function hash(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
